import posthog from "posthog-js";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { ADMIN_PATH } from "src/themeLayouts";

/**
 * Capture a PostHog `$pageview` on every student-facing route change.
 *
 * `posthog-js` only auto-captures the pageview for the initial document load,
 * so in an SPA every subsequent route was invisible: `/onboarding/survey` had
 * 129 pageviews against 1,167 people who reached it. That gap is what makes
 * campaign funnels unreadable — a UTM-tagged visitor lands on `/register` (a
 * real page load, so that one is captured), then every step after it silently
 * drops out of the funnel.
 *
 * The campaign properties themselves need no help: `posthog-js` stashes the
 * landing URL's `utm_*` params in its own persistence and replays them onto
 * every event in the session, and sets both `$initial_utm_*` (first touch) and
 * `utm_*` (last touch) on the person. Restoring the missing pageviews is all
 * that's required to segment a funnel by campaign.
 *
 * Amplitude is deliberately not called here — it is initialized with
 * `defaultTracking: true`, which already records SPA page views. Sending a
 * second one would double-count.
 */
export function usePageViewTracking(): void {
  const { pathname } = useLocation();
  const hasSeenInitialRoute = useRef(false);

  useEffect(() => {
    // The landing route is already captured by `posthog-js` on init. Counting
    // it again here would double every session's entry page.
    if (!hasSeenInitialRoute.current) {
      hasSeenInitialRoute.current = true;
      return;
    }

    // Instrumentation is for product analytics on student-facing surfaces.
    if (pathname.startsWith(ADMIN_PATH)) return;

    posthog.capture("$pageview", { $current_url: window.location.href });
  }, [pathname]);
}

export default usePageViewTracking;
